import * as XLSXStyle from "xlsx-js-style";
import {
  FORMATO_CONTABILIDAD,
  headerStyle,
  textStyle,
  numeroStyle,
  moneyStyle,
} from "../../../Components/ExcelEstilos";

/**
 * Plomeria compartida de las CUATRO exportaciones de Listas de Precios
 * (Descargar, Modificar Precios, Fijar Porcentajes y Analizar Diferencias).
 *
 * Las cuatro repetian el mismo bloque de columnas base copiado a mano, asi que
 * agregarle una columna al catalogo significaba tocar cuatro archivos y
 * arriesgarse a que uno quedara distinto. Aqui vive ese bloque una sola vez;
 * cada pantalla sigue armando sus propias columnas por lista de precios.
 *
 * DOS COSAS QUE NO SE PUEDEN CAMBIAR:
 *
 * 1. Los encabezados van en la FILA 1. Dos de estos archivos se vuelven a subir,
 *    y el importador los lee con `sheet_to_row_object_array`, que toma la primera
 *    fila como nombres de columna. Por eso aqui NO hay banda de encabezado con
 *    empresa/titulo/periodo como en los reportes: rompiendo eso, la plantilla
 *    deja de poder re-importarse.
 *
 * 2. Ningun encabezado nuevo puede EMPEZAR con "Precio ", "%F " o "%R ". El
 *    importador identifica las columnas de cada lista por ese prefijo y le
 *    quita el prefijo para buscar la lista por nombre; un encabezado que lo
 *    imite dispara "El precio X no existe" en todas las filas e invalida el
 *    archivo completo.
 */

/** Columnas base, en orden. Las cuatro exportaciones comparten exactamente estas. */
export const ENCABEZADOS_BASE = [
  "Codigo",
  "Clave",
  "Equivalencia",
  "Codigo de Barras",
  "Descripcion",
  "Descripcion Larga",
  "Unidad",
  "Marca",
  "Costo",
  "Costo Catalogo",
  "Factor",
  "Ultimo Costo Compra",
];

/**
 * Identificadores: se escriben como TEXTO explicito.
 *
 * `Insumo.codigo` es un Number en el esquema, asi que `json_to_sheet` lo escribe
 * como numero y un codigo de 11 digitos aparece como `1.23457E+10` en formato
 * General. El `numFmt: "@"` ademas evita que Excel lo reinterprete si el usuario
 * lo edita despues de descargar.
 */
const COLUMNAS_TEXTO = ["Codigo", "Clave", "Codigo de Barras"];

/** Columnas de dinero fijas; las de cada lista se detectan por el prefijo "Precio ". */
const COLUMNAS_MONEDA = ["Costo", "Costo Catalogo", "Ultimo Costo Compra"];

/** Porcentajes y factores: numero simple con dos decimales, no moneda. */
const COLUMNAS_NUMERO = ["Factor"];

const ANCHOS = {
  Codigo: 14,
  Clave: 18,
  Equivalencia: 12,
  "Codigo de Barras": 18,
  Descripcion: 40,
  "Descripcion Larga": 46,
  Unidad: 12,
  Marca: 18,
  Costo: 14,
  "Costo Catalogo": 16,
  Factor: 9,
  "Ultimo Costo Compra": 18,
};

const textoStyle = { ...textStyle, numFmt: "@" };

/**
 * El bloque de columnas base de un renglon.
 *
 * `Costo` es el costo en la unidad del renglon: en una equivalencia el backend ya
 * lo dividio entre el factor (la equivalencia es la unidad menor). `Costo
 * Catalogo` es el costo integro del insumo padre y `Factor` el divisor, para que
 * los dos numeros se puedan leer juntos: un espiral de 260.00 el rollo que
 * aparece en 2.60 con factor 100 es el mismo costo en metros, no una
 * contradiccion. Si el renglon de una presentacion que se vende por rollo sale
 * con factor 100, lo que esta al reves es la captura de la equivalencia.
 */
export const columnasBase = (valueInsumo) => ({
  Codigo: valueInsumo.codigo,
  Clave: valueInsumo.clave,
  Equivalencia: valueInsumo.noPrincipal ? "Si" : "No",
  "Codigo de Barras": valueInsumo.codigoBarras,
  Descripcion: valueInsumo.descripcion,
  "Descripcion Larga": valueInsumo.descripcionLarga,
  Unidad: valueInsumo.unidad,
  Marca: valueInsumo?.marca?.nombre ? valueInsumo.marca.nombre : "",
  Costo: valueInsumo.costo,
  "Costo Catalogo": valueInsumo.costoCatalogo,
  Factor: valueInsumo.factor,
  "Ultimo Costo Compra": valueInsumo.ultimoCostoCompra ?? undefined,
});

/**
 * El margen que el precio de una lista deja contra el ULTIMO COSTO DE COMPRA, no
 * contra el costo con el que se capturo.
 *
 * El backend ya lo calcula respetando `Empresa.calcularPreciosSobre` (sobre costo
 * o sobre precio de venta), que es el mismo switch con el que se capturaron los
 * porcentajes de la lista; asi los dos numeros son comparables. Viene en null
 * cuando el insumo no tiene compras registradas o la lista no tiene precio.
 */
export const columnaMargenReal = (valueInsumo, precioEmpresa) => ({
  [`Margen Real ${precioEmpresa.nombre}`]:
    valueInsumo[`margen_real_${precioEmpresa._id}`] ?? undefined,
});

/** Encabezados en orden estable: primero los base, luego los de cada lista. */
const encabezadosDe = (filas) => {
  const vistos = [];
  const registrados = new Set();
  const registrar = (key) => {
    if (registrados.has(key)) return;
    registrados.add(key);
    vistos.push(key);
  };

  // Los base van primero aunque un renglon suelto no traiga alguno; sin esto el
  // orden lo dictaria el primer renglon que tuviera todas las claves.
  ENCABEZADOS_BASE.forEach(registrar);
  for (const fila of filas) Object.keys(fila).forEach(registrar);
  return vistos;
};

const estiloDeColumna = (encabezado) => {
  if (COLUMNAS_TEXTO.includes(encabezado)) return textoStyle;
  if (COLUMNAS_MONEDA.includes(encabezado) || encabezado.startsWith("Precio ")) {
    return moneyStyle;
  }
  if (COLUMNAS_NUMERO.includes(encabezado) || encabezado.startsWith("Margen Real ")) {
    return { ...numeroStyle, numFmt: "0.00" };
  }
  return textStyle;
};

/**
 * Convierte las filas en una hoja ya estilizada, con los encabezados en la fila 1.
 *
 * El post-proceso sobre las celdas es obligatorio y no cosmetico: `json_to_sheet`
 * no acepta celdas pre-armadas, asi que el tipo y el formato solo se pueden
 * corregir despues de crearla.
 */
export const construirHojaPrecios = (filas) => {
  const encabezados = encabezadosDe(filas);
  const hoja = XLSXStyle.utils.json_to_sheet(filas, { header: encabezados });

  hoja["!cols"] = encabezados.map((encabezado) => ({
    wch: ANCHOS[encabezado] || Math.max(12, encabezado.length + 2),
  }));

  const rango = XLSXStyle.utils.decode_range(hoja["!ref"] || "A1");
  for (let columna = rango.s.c; columna <= rango.e.c; columna++) {
    const encabezado = encabezados[columna];
    if (encabezado === undefined) continue;
    const estilo = estiloDeColumna(encabezado);
    const esTexto = COLUMNAS_TEXTO.includes(encabezado);

    for (let fila = rango.s.r; fila <= rango.e.r; fila++) {
      const referencia = XLSXStyle.utils.encode_cell({ r: fila, c: columna });
      const celda = hoja[referencia];
      if (!celda) continue;

      if (fila === rango.s.r) {
        celda.s = headerStyle;
        continue;
      }

      if (esTexto) {
        // `t: "s"` ademas del numFmt: sin el, Excel sigue guardando el numero y
        // el formato de texto solo cambia como se ve.
        celda.v = celda.v === undefined || celda.v === null ? "" : String(celda.v);
        celda.t = "s";
        celda.z = "@";
      } else if (estilo === moneyStyle) {
        celda.z = FORMATO_CONTABILIDAD;
      }
      celda.s = estilo;
    }
  }

  return hoja;
};

/**
 * Una hoja de una sola columna de texto, para las instrucciones de la plantilla.
 * Mismo encabezado turquesa que la hoja de datos, para que el libro se vea de una
 * pieza.
 */
export const construirHojaInstrucciones = (encabezado, lineas) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([
    [{ v: encabezado, t: "s", s: headerStyle }],
    ...lineas.map((linea) => [{ v: linea, t: "s", s: textStyle }]),
  ]);
  hoja["!cols"] = [{ wch: 120 }];
  return hoja;
};

/** Descarga el libro. `hojas` es `[{ nombre, filas }]` o `[{ nombre, hoja }]`. */
export const descargarLibroPrecios = (hojas, nombreArchivo) => {
  const libro = XLSXStyle.utils.book_new();
  for (const { nombre, filas, hoja } of hojas) {
    XLSXStyle.utils.book_append_sheet(
      libro,
      hoja || construirHojaPrecios(filas || []),
      nombre
    );
  }
  XLSXStyle.writeFile(libro, `${nombreArchivo}.xlsx`);
};
